// Facility Medication Settings — med-pass schedule (mig 208). Config over code: passes + window + frequency map,
// plus "Align active orders" (preview → select → PIN). Mobile-first; no facility hardcodes.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const NAVY = '#0A2239', BLUE = '#0878E8';
const card = { background:'#fff', border:'1px solid #e6e9ee', borderRadius:12, padding:16, marginBottom:14 };
const inp = { padding:'8px 10px', border:'1px solid #d5dbe3', borderRadius:8, fontSize:14, fontFamily:'Inter, system-ui, sans-serif' };
const btn = (primary) => ({ padding:'8px 14px', borderRadius:8, border: primary ? 'none' : '1px solid #d5dbe3', background: primary ? BLUE : '#fff', color: primary ? '#fff' : NAVY, fontWeight:600, fontSize:13, cursor:'pointer' });
const muted = { color:'#5b6b7c', fontSize:12 };

export default function MedPassSettings({ api, facilityId, facilityName }) {
  const [cfg, setCfg] = useState(null);
  const [passes, setPasses] = useState([]);
  const [win, setWin] = useState(60);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [picked, setPicked] = useState({});
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId) return;
    try {
      const { data } = await axios.get(`${api}/api/emr/med-pass/${facilityId}`);
      setCfg(data); setPasses(data.passes.map(p => ({ ...p }))); setWin(data.med_window_minutes);
    } catch (e) { setMsg({ err: e.response?.data?.error || e.message }); }
  }, [api, facilityId]);
  useEffect(() => { load(); setPreview(null); setMsg(null); }, [load]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const { data } = await axios.put(`${api}/api/emr/med-pass/${facilityId}`, { passes, frequency_map: cfg.frequency_map, med_window_minutes: Number(win) });
      setCfg(data); setPasses(data.passes.map(p => ({ ...p }))); setMsg({ ok: 'Saved. New orders prefill from these passes.' }); setPreview(null);
    } catch (e) { setMsg({ err: e.response?.data?.error || e.message }); } finally { setSaving(false); }
  };
  const loadPreview = async () => {
    setMsg(null);
    try {
      const { data } = await axios.get(`${api}/api/emr/med-pass/${facilityId}/realign-preview`);
      setPreview(data); const all = {}; data.to_change.forEach(r => { all[r.segment_id] = true; }); setPicked(all);
    } catch (e) { setMsg({ err: e.response?.data?.error || e.message }); }
  };
  const apply = async () => {
    setApplying(true);
    try {
      const segment_ids = Object.keys(picked).filter(k => picked[k]);
      const { data } = await axios.post(`${api}/api/emr/med-pass/${facilityId}/realign`, { segment_ids, pin });
      setPinOpen(false); setPin(''); setPreview(null);
      setMsg({ ok: `${data.realigned} order segment(s) aligned — new times start ${data.effective_date}. ${data.providers_notified} prescriber(s) notified.` });
    } catch (e) { setMsg({ err: e.response?.data?.error || e.message }); } finally { setApplying(false); }
  };

  if (!facilityId) return <div style={muted}>Pick a single facility to edit its medication settings.</div>;
  if (!cfg) return <div style={muted}>{msg?.err || 'Loading…'}</div>;
  const canEdit = cfg.can_edit;
  const nChosen = Object.values(picked).filter(Boolean).length;

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap', gap:8 }}>
          <div style={{ fontWeight:700, color:NAVY, fontSize:16 }}>Medication passes — {facilityName || cfg.org_name}</div>
          {cfg.is_default && <span style={{ ...muted, color:'#b45309' }}>Using platform defaults — set this facility's real pass times</span>}
        </div>
        <div style={{ ...muted, marginTop:4 }}>Orders prefill from these passes by frequency (BID → morning + evening, TID → all three). Prescribers can still type their own times; those are marked as overrides and never realigned.</div>
        <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:8 }}>
          {passes.map((p, i) => (
            <div key={i} style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <input style={{ ...inp, width:150 }} value={p.label} disabled={!canEdit} placeholder="Label (Morning)"
                onChange={e => { const g=[...passes]; g[i]={...p, label:e.target.value}; setPasses(g); }} />
              <input style={{ ...inp, width:110 }} type="time" value={p.at} disabled={!canEdit}
                onChange={e => { const g=[...passes]; g[i]={...p, at:e.target.value}; setPasses(g); }} />
              <input style={{ ...inp, width:80 }} value={p.key} disabled={!canEdit} placeholder="key" title="Short id used by the frequency map (am, mid, pm, hs)"
                onChange={e => { const g=[...passes]; g[i]={...p, key:e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,'')}; setPasses(g); }} />
              {canEdit && passes.length > 1 && <button style={btn(false)} onClick={() => setPasses(passes.filter((_, j) => j !== i))}>×</button>}
            </div>
          ))}
          {canEdit && <div><button style={btn(false)} onClick={() => setPasses([...passes, { key:'hs', label:'Bedtime', at:'22:00' }])}>+ Add pass</button></div>}
        </div>
        <div style={{ marginTop:14, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <label style={{ fontSize:13, color:NAVY, fontWeight:600 }}>Window ±</label>
          <input style={{ ...inp, width:80 }} type="number" min={0} max={240} value={win} disabled={!canEdit} onChange={e => setWin(e.target.value)} />
          <span style={muted}>minutes either side of the pass time (0 disables enforcement)</span>
        </div>
        {canEdit && <div style={{ marginTop:14, display:'flex', gap:8, alignItems:'center' }}>
          <button style={btn(true)} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save passes'}</button>
          <button style={btn(false)} onClick={load}>Reset</button>
        </div>}
        {msg?.ok && <div style={{ marginTop:10, color:'#047857', fontSize:13 }}>{msg.ok}</div>}
        {msg?.err && <div style={{ marginTop:10, color:'#b91c1c', fontSize:13 }}>{msg.err}</div>}
      </div>

      {canEdit && <div style={card}>
        <div style={{ fontWeight:700, color:NAVY }}>Align active orders to these passes</div>
        <div style={{ ...muted, marginTop:4 }}>Shows every active scheduled order whose times differ from the passes. Each dose keeps its count and snaps to the nearest pass; new times start <b>tomorrow</b> — today's MAR is never changed. Tapers, PRN, interval frequencies (q6h) and prescriber overrides are left alone.</div>
        <div style={{ marginTop:10 }}><button style={btn(false)} onClick={loadPreview}>Preview changes</button></div>
        {preview && (
          <div style={{ marginTop:12 }}>
            {preview.to_change.length === 0 ? <div style={{ color:'#047857', fontSize:13 }}>All active scheduled orders already match the passes ({preview.already_aligned} checked).</div> : (
              <>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead><tr style={{ textAlign:'left', color:'#5b6b7c' }}>
                      <th style={{ padding:6 }}><input type="checkbox" checked={nChosen === preview.to_change.length} onChange={e => { const all={}; preview.to_change.forEach(r => { all[r.segment_id]=e.target.checked; }); setPicked(all); }} /></th>
                      <th style={{ padding:6 }}>Client</th><th style={{ padding:6 }}>Medication</th><th style={{ padding:6 }}>Freq</th><th style={{ padding:6 }}>Now</th><th style={{ padding:6 }}>Becomes</th>
                    </tr></thead>
                    <tbody>{preview.to_change.map(r => (
                      <tr key={r.segment_id} style={{ borderTop:'1px solid #eef1f4' }}>
                        <td style={{ padding:6 }}><input type="checkbox" checked={!!picked[r.segment_id]} onChange={e => setPicked({ ...picked, [r.segment_id]: e.target.checked })} /></td>
                        <td style={{ padding:6 }}>{r.patient_name}</td><td style={{ padding:6 }}>{r.medication_name}</td><td style={{ padding:6 }}>{r.frequency}</td>
                        <td style={{ padding:6, color:'#b45309' }}>{r.current_times.join(' · ') || '—'}</td>
                        <td style={{ padding:6, color:'#047857', fontWeight:600 }}>{r.target_times.join(' · ')}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
                <div style={{ marginTop:10, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                  <button style={btn(true)} disabled={!nChosen} onClick={() => setPinOpen(true)}>Align {nChosen} order{nChosen === 1 ? '' : 's'} from {preview.effective_date}</button>
                  <span style={muted}>Signed with your PIN · audited · ordering prescribers are notified</span>
                </div>
              </>
            )}
            {preview.unmapped?.length > 0 && <details style={{ marginTop:10 }}><summary style={{ ...muted, cursor:'pointer' }}>{preview.unmapped.length} order(s) left to the prescriber (frequency not on the pass map)</summary>
              <ul style={{ ...muted, marginTop:6 }}>{preview.unmapped.map((u, i) => <li key={i}>{u.patient_name} — {u.medication_name} — "{u.frequency}"</li>)}</ul></details>}
          </div>
        )}
      </div>}

      {pinOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(10,34,57,.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div style={{ ...card, width:'100%', maxWidth:380, marginBottom:0 }}>
            <div style={{ fontWeight:700, color:NAVY }}>Sign the realignment</div>
            <div style={{ ...muted, marginTop:4 }}>{nChosen} order segment(s) → new times from {preview?.effective_date}. Enter your signature PIN.</div>
            <input style={{ ...inp, width:'100%', marginTop:12, fontSize:18, letterSpacing:4, textAlign:'center' }} type="password" inputMode="numeric" autoFocus value={pin} onChange={e => setPin(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && pin) apply(); }} />
            {msg?.err && <div style={{ marginTop:8, color:'#b91c1c', fontSize:13 }}>{msg.err}</div>}
            <div style={{ marginTop:12, display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button style={btn(false)} onClick={() => { setPinOpen(false); setPin(''); }}>Cancel</button>
              <button style={btn(true)} disabled={!pin || applying} onClick={apply}>{applying ? 'Applying…' : 'Sign & align'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
